import React, { useEffect, useState } from 'react';
import { Gauge, Clock } from 'lucide-react';
import WidgetCard from './WidgetCard';
import { useAuthStore } from '../../../store/useAuthStore';
import { getCreditBalance, getCreditUsageHistory } from '../../../services/creditService';
import { CreditBalance, MonthlyUsage } from '../../../types/credits';

export default function CreditsUsageWidget() {
  const { user } = useAuthStore();
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [usage, setUsage] = useState<MonthlyUsage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      setLoading(true);
      const [balRes, usageRes] = await Promise.all([
        getCreditBalance(user.id),
        getCreditUsageHistory(user.id, 6),
      ]);
      if (balRes.data) setBalance(balRes.data);
      if (usageRes.data) setUsage(usageRes.data);
      setLoading(false);
    };

    fetchData();
  }, [user]);

  const tier = user?.subscription_tier || 'free';
  const isSubscriber = tier === 'pro' || tier === 'enterprise';

  // Calculate gauge percentage
  const gaugePercent = balance
    ? balance.total > 0
      ? Math.round((balance.remaining / balance.total) * 100)
      : 0
    : 0;

  const maxBarUsage = Math.max(...usage.map((u) => u.count), 1);

  return (
    <WidgetCard
      title="Credits & Usage"
      icon={<Gauge className="w-5 h-5" />}
      isLoading={loading}
    >
      {!isSubscriber && balance?.total === 0 ? (
        // Free tier / no credits
        <div className="text-center space-y-3">
          <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto">
            <Gauge className="w-8 h-8 text-gray-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">No Credits</p>
            <p className="text-xs text-gray-500 mt-1">
              Purchase a single report or subscribe for monthly credits.
            </p>
          </div>
          <a
            href="/pricing"
            className="inline-block text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            View Pricing
          </a>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Credit gauge */}
          <div className="text-center">
            <div className="relative w-24 h-24 mx-auto">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="8"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke={gaugePercent > 20 ? '#3b82f6' : '#ef4444'}
                  strokeWidth="8"
                  strokeDasharray={`${gaugePercent * 2.64} 264`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xl font-bold text-gray-900">
                  {balance?.remaining ?? 0}
                </span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              of {balance?.total ?? 0} credits remaining
            </p>
          </div>

          {/* Rollover info */}
          {balance && balance.rolloverCredits > 0 && (
            <div className="flex items-center gap-2 text-xs bg-amber-50 text-amber-700 p-2 rounded-lg">
              <Clock className="w-3.5 h-3.5 flex-shrink-0" />
              <span>
                {balance.rolloverCredits} rollover credit{balance.rolloverCredits !== 1 ? 's' : ''}
                {balance.rolloverExpiresAt && (
                  <>
                    {' '}expire{' '}
                    {new Date(balance.rolloverExpiresAt).toLocaleDateString()}
                  </>
                )}
              </span>
            </div>
          )}

          {/* Mini usage chart */}
          {usage.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-2">Monthly Usage</p>
              <div className="flex items-end gap-1 h-12">
                {usage.map((m) => (
                  <div
                    key={m.month}
                    className="flex-1 bg-blue-200 rounded-t hover:bg-blue-300 transition-colors relative group"
                    style={{
                      height: `${Math.max((m.count / maxBarUsage) * 100, 4)}%`,
                    }}
                  >
                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[10px] px-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      {m.count}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                <span>{usage[0]?.month.split('-')[1]}</span>
                <span>{usage[usage.length - 1]?.month.split('-')[1]}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </WidgetCard>
  );
}
